import { useEffect, useMemo, useState } from 'react';
import { isDiamondCode, SEDE_CODES } from '@lidotel/shared';
import { api, type ResStatExtraction } from '../../lib/api.js';

const empty: ResStatExtraction = {
  sede_codigo: null,
  formato: 'semanal',
  fechas: [],
  rate_codes: [],
  grand_total_rooms: [],
};

/**
 * Revisión del Reservation Statistics 2: PDF a la izquierda, tabla de rate codes
 * a la derecha. Diamond NO se teclea: es la suma de los códigos DIAM* y se
 * muestra desglosada, así se ve de dónde sale cada número.
 *
 * Un archivo puede traer días ya cargados (el del lunes repite toda la semana).
 * Esos días salen marcados y NO se reescriben salvo que se marque la casilla:
 * re-pisar un día ya revisado tiene que ser una decisión, no un efecto colateral.
 */
export function ResStatReviewPanel({
  id,
  onCommitted,
  onClose,
}: {
  id: string;
  onCommitted: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<ResStatExtraction>(empty);
  const [yaCargados, setYaCargados] = useState<Map<string, number>>(new Map());
  const [sobreescribir, setSobreescribir] = useState<Set<string>>(new Set());
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [needsExtract, setNeedsExtract] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El backend bloquea días imposibles (p. ej. una columna acumulada leída como
  // día). Se ofrece forzar solo después de haber visto el motivo.
  const [blocked, setBlocked] = useState(false);

  function hydrate(d: ResStatExtraction) {
    setForm({
      sede_codigo: d.sede_codigo ?? null,
      formato: d.formato ?? 'semanal',
      fechas: d.fechas ?? [],
      rate_codes: d.rate_codes ?? [],
      grand_total_rooms: d.grand_total_rooms?.length
        ? d.grand_total_rooms
        : (d.fechas ?? []).map(() => null),
    });
  }

  useEffect(() => {
    let url: string | null = null;
    api
      .ingestaGet(id)
      .then((r) => {
        if (r.extracted && 'fechas' in r.extracted) hydrate(r.extracted as ResStatExtraction);
        else setNeedsExtract(true);
        setYaCargados(new Map((r.yaCargados ?? []).map((d) => [d.fecha, d.diamond])));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
    api
      .ingestaFileUrl(id)
      .then((u) => {
        url = u;
        setPdfUrl(u);
      })
      .catch(() => setPdfUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);

  async function extract() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaExtract(id);
      if (r.tipo === 'diamond') hydrate(r.extracted as ResStatExtraction);
      setNeedsExtract(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error de extracción');
    } finally {
      setBusy(false);
    }
  }

  /** Corrección manual de una celda de rate code (fila × día). */
  function setCelda(fila: number, col: number, raw: string) {
    const value = raw === '' ? null : Number(raw);
    setForm((f) => {
      const rate_codes = f.rate_codes.map((rc, i) => {
        if (i !== fila) return rc;
        const rooms = [...rc.rooms];
        rooms[col] = value;
        return { ...rc, rooms };
      });
      return { ...f, rate_codes };
    });
  }

  function toggleSobreescribir(fecha: string) {
    setSobreescribir((prev) => {
      const next = new Set(prev);
      if (next.has(fecha)) next.delete(fecha);
      else next.add(fecha);
      return next;
    });
  }

  async function commit(force: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaCommitResStat(
        id,
        { ...form, sobreescribir: [...sobreescribir] },
        force,
      );
      setIssues(r.issues ?? []);
      if (r.committed) onCommitted();
      else if (r.error) {
        setError(r.error);
        setBlocked(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al confirmar');
    } finally {
      setBusy(false);
    }
  }

  const dias = form.fechas;
  const diamondPorDia = useMemo(
    () =>
      dias.map((_, i) =>
        form.rate_codes
          .filter((rc) => isDiamondCode(rc.code))
          .reduce((s, rc) => s + (rc.rooms[i] ?? 0), 0),
      ),
    [dias, form.rate_codes],
  );
  const nuevos = dias.filter((f) => !yaCargados.has(f) || sobreescribir.has(f));

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-200">
          Revisión Reservation Statistics 2
          {form.formato === 'mtd' && (
            <span className="ml-2 rounded bg-amber-900 px-1.5 py-0.5 text-[10px] text-amber-200">
              formato DAY/MONTH/YEAR · solo el día
            </span>
          )}
        </h2>
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
          Cerrar ✕
        </button>
      </div>

      {error && (
        <p className="mb-3 rounded-lg bg-rose-950/60 px-3 py-2 text-sm text-rose-300">{error}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-h-[420px] overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Reservation Statistics"
              className="h-[420px] w-full lg:h-[640px]"
            />
          ) : (
            <p className="p-4 text-sm text-slate-500">Vista previa no disponible.</p>
          )}
        </div>

        <div>
          {needsExtract && (
            <div className="mb-4">
              <p className="mb-2 text-sm text-slate-400">Aún no se ha extraído este reporte.</p>
              <button
                onClick={extract}
                disabled={busy}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {busy ? 'Extrayendo…' : 'Extraer con Gemini'}
              </button>
            </div>
          )}

          <label className="mb-3 block text-xs text-slate-400">
            Sede
            <select
              value={form.sede_codigo ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, sede_codigo: e.target.value || null }))}
              className="mt-1 block w-40 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
            >
              <option value="">—</option>
              {SEDE_CODES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          {dias.length === 0 ? (
            <p className="text-sm text-slate-500">
              Sin días detectados. Extrae el reporte primero.
            </p>
          ) : (
            <>
              <div className="max-h-[360px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-slate-500">
                      <th className="px-1 py-1 text-left">Rate code</th>
                      {dias.map((d) => (
                        <th key={d} className="px-1 py-1 text-right">
                          {d.slice(5)}
                          {yaCargados.has(d) && (
                            <span
                              title={`Ya cargado: ${yaCargados.get(d)} noches Diamond`}
                              className="ml-1 text-amber-400"
                            >
                              ●
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.rate_codes.map((rc, fila) => {
                      const esDiamond = isDiamondCode(rc.code);
                      return (
                        <tr
                          key={`${rc.code}-${fila}`}
                          className={`border-t border-slate-800 ${
                            esDiamond ? 'bg-violet-950/40 text-violet-100' : 'text-slate-400'
                          }`}
                        >
                          <td className="px-1 py-1 font-mono">{rc.code}</td>
                          {dias.map((_, i) => (
                            <td key={i} className="px-1 py-1 text-right">
                              {esDiamond ? (
                                <input
                                  type="number"
                                  min="0"
                                  value={rc.rooms[i] ?? ''}
                                  onChange={(e) => setCelda(fila, i, e.target.value)}
                                  className="w-14 rounded border border-slate-700 bg-slate-900 px-1 py-0.5 text-right text-slate-100"
                                />
                              ) : (
                                (rc.rooms[i] ?? 0)
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    <tr className="border-t border-slate-600 font-semibold text-slate-100">
                      <td className="px-1 py-1">Noches Diamond</td>
                      {diamondPorDia.map((v, i) => (
                        <td key={i} className="px-1 py-1 text-right">
                          {v}
                        </td>
                      ))}
                    </tr>
                    <tr className="text-slate-500">
                      <td className="px-1 py-1">Grand Total (todo el hotel)</td>
                      {dias.map((_, i) => (
                        <td key={i} className="px-1 py-1 text-right">
                          {form.grand_total_rooms[i] ?? '—'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/60 p-2 text-xs">
                <p className="mb-1 text-slate-400">
                  Se van a guardar <span className="text-slate-100">{nuevos.length}</span> de{' '}
                  {dias.length} día(s).
                </p>
                {dias
                  .filter((f) => yaCargados.has(f))
                  .map((f) => (
                    <label key={f} className="flex items-center gap-2 py-0.5 text-amber-200">
                      <input
                        type="checkbox"
                        checked={sobreescribir.has(f)}
                        onChange={() => toggleSobreescribir(f)}
                      />
                      <span>
                        {f} ya tiene {yaCargados.get(f)} noches Diamond cargadas — sobreescribir
                      </span>
                    </label>
                  ))}
              </div>
            </>
          )}

          {issues.length > 0 && (
            <ul className="mt-3 space-y-1">
              {issues.map((it, idx) => (
                <li
                  key={idx}
                  className="rounded-md bg-amber-950/50 px-2 py-1 text-xs text-amber-200"
                >
                  {it}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => commit(false)}
              disabled={busy || needsExtract || dias.length === 0}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {busy ? 'Confirmando…' : `Confirmar ${nuevos.length} día(s)`}
            </button>
            {blocked && (
              <button
                onClick={() => commit(true)}
                disabled={busy}
                title="Guarda los valores tal cual, saltándose las validaciones"
                className="rounded-md border border-amber-600 px-3 py-1.5 text-sm text-amber-300 hover:bg-amber-950/40 disabled:opacity-50"
              >
                Confirmar de todos modos
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Diamond = suma de los rate codes DIAM* (resaltados y editables). El detalle de todos los
            códigos se guarda para auditoría.
          </p>
        </div>
      </div>
    </div>
  );
}
